import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createAvatarSession,
  fetchAvatarCatalog,
  fetchVoicePreviewMp3,
  listVideos,
  type AvatarCatalog,
  type Video,
} from "../api";
import { formatBytes, relativeTime } from "../utils";
import PersonaPicker from "../components/avatar/PersonaPicker";
import BackgroundPicker from "../components/avatar/BackgroundPicker";
import {
  BG_CATALOG,
  PERSONA_CATALOG,
} from "../components/avatar/catalog";


const VOICE_FALLBACK: Record<string, { name: string; meta: string }> = {
  "vox-04": { name: "Vox 04", meta: "sec · grave" },
  "vox-07": { name: "Vox 07", meta: "doux · médium" },
  "vox-12": { name: "Vox 12", meta: "vif · clair" },
};

const VOICE_ORDER = ["vox-04", "vox-07", "vox-12"] as const;

function MiniWaveform({ active }: { active: boolean }) {
  const bars = 36;
  return (
    <div className="flex h-8 items-end gap-px bg-charcoal/10 px-1 py-1 rounded">
      {Array.from({ length: bars }).map((_, i) => {
        const h = 4 + Math.abs(Math.sin(i * 0.45)) * 20;
        return (
          <div
            key={i}
            className="w-0.5 rounded-sm bg-charcoal"
            style={{
              height: `${h}px`,
              opacity: active ? 1 : 0.35,
            }}
          />
        );
      })}
    </div>
  );
}

export default function AvatarBuilderPanel({
  onSessionCreated,
}: {
  /** If set, called instead of internal navigation (e.g. stay in embedded context). */
  onSessionCreated?: (sessionId: string, sharePath: string) => void;
}) {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const seededReuseVideoId = searchParams.get("reuseVideoId");

  const [catalog, setCatalog] = useState<AvatarCatalog | null>(null);
  const [catalogErr, setCatalogErr] = useState<string | null>(null);

  // Multi-select state — additive matrix (preview UI).
  // Backend currently consumes single ids; submit logic below picks the first
  // element of each list for back-compat. Multi-batch wiring will land later.
  const [personIds, setPersonIds] = useState<string[]>(["01_henri"]);
  const [bgIds, setBgIds] = useState<string[]>(["fond1_bureau_vitre"]);
  const [voice, setVoice] = useState<string>("vox-04");
  const [langs, setLangs] = useState<string[]>(["fr"]);

  const [script, setScript] = useState(
    "Salut, je te partage un message rapide — merci d’être là."
  );
  const [advancedScripts, setAdvancedScripts] = useState(false);
  const [byLang, setByLang] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState<string | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const previewObjectUrlRef = useRef<string | null>(null);

  const [videoSource, setVideoSource] = useState<"new" | "reuse">(
    seededReuseVideoId ? "reuse" : "new"
  );
  const [recentVideos, setRecentVideos] = useState<Video[] | null>(null);
  const [recentVideosErr, setRecentVideosErr] = useState<string | null>(null);
  const [reuseVideoId, setReuseVideoId] = useState<string | null>(
    seededReuseVideoId
  );

  const stopVoicePreview = useCallback(() => {
    if (previewObjectUrlRef.current) {
      URL.revokeObjectURL(previewObjectUrlRef.current);
      previewObjectUrlRef.current = null;
    }
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewingVoice(null);
  }, []);

  useEffect(() => () => stopVoicePreview(), [stopVoicePreview]);

  const toggleVoicePreview = async (vid: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPreviewErr(null);
    if (previewingVoice === vid && previewAudioRef.current) {
      stopVoicePreview();
      return;
    }
    stopVoicePreview();
    setPreviewLoading(vid);
    try {
      const blob = await fetchVoicePreviewMp3(vid);
      const url = URL.createObjectURL(blob);
      previewObjectUrlRef.current = url;
      const audio = new Audio(url);
      previewAudioRef.current = audio;
      audio.onended = () => stopVoicePreview();
      audio.onerror = () => {
        setPreviewErr("Lecture audio impossible");
        stopVoicePreview();
      };
      setPreviewingVoice(vid);
      await audio.play();
    } catch (ex: unknown) {
      setPreviewErr(
        ex instanceof Error ? ex.message : "Impossible de charger l’extrait"
      );
      stopVoicePreview();
    } finally {
      setPreviewLoading(null);
    }
  };

  const loadCatalog = useCallback(() => {
    fetchAvatarCatalog()
      .then(setCatalog)
      .catch((e: unknown) =>
        setCatalogErr(e instanceof Error ? e.message : "Catalogue indisponible")
      );
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    if (videoSource !== "reuse") return;
    let cancelled = false;
    setRecentVideosErr(null);
    setRecentVideos(null);
    listVideos(undefined, 10)
      .then((rows) => {
        if (!cancelled) setRecentVideos(rows);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setRecentVideosErr(
            e instanceof Error ? e.message : "Impossible de charger les vidéos"
          );
      });
    return () => {
      cancelled = true;
    };
  }, [videoSource]);

  // Catalog is informational only at the moment — the new 12-persona / 8-bg
  // catalog is rendered from PERSONA_CATALOG and BG_CATALOG above, with
  // thumbnails served from /public/avatar-assets/. Backend catalog will catch
  // up at Bloc 1 (extended avatar_catalog.py + S3-served thumbnails).
  void catalog;

  const languageOptions = catalog?.language_options ?? [
    { code: "fr", label: "Français" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
    { code: "de", label: "Deutsch" },
    { code: "he", label: "עברית" },
  ];

  const toggleLang = (code: string) => {
    const order = languageOptions.map((o) => o.code);
    const rank = (c: string) => {
      const i = order.indexOf(c);
      return i === -1 ? 999 : i;
    };
    setLangs((prev) => {
      const has = prev.includes(code);
      if (has) {
        const next = prev.filter((c) => c !== code);
        return next.length ? next : prev;
      }
      if (prev.length >= 5) return prev;
      return [...prev, code].sort((a, b) => rank(a) - rank(b));
    });
  };

  const jobCount = personIds.length * bgIds.length * langs.length;
  const isBatch = personIds.length > 1 || bgIds.length > 1;

  const submit = async () => {
    setErr(null);
    if (videoSource === "reuse" && !reuseVideoId) {
      setErr("Choisis une vidéo dans la liste ou passe en nouvel enregistrement.");
      return;
    }
    if (isBatch) {
      setErr(
        "Multi-batch (preview UI uniquement) — le backend batch n’est pas encore branché. Choisis 1 personnage et 1 fond pour générer maintenant."
      );
      return;
    }
    setBusy(true);
    try {
      const script_by_lang: Record<string, string> = {};
      if (advancedScripts) {
        for (const code of langs) {
          const t = (byLang[code] ?? "").trim();
          if (t) script_by_lang[code] = t;
        }
      }
      const r = await createAvatarSession({
        person_id: personIds[0],
        bg_id: bgIds[0],
        voice_id: voice,
        langs,
        script_text: script.trim(),
        ...(Object.keys(script_by_lang).length ? { script_by_lang } : {}),
        ...(videoSource === "reuse" && reuseVideoId
          ? { reuse_video_id: reuseVideoId }
          : {}),
      });
      if (onSessionCreated) {
        onSessionCreated(r.session_id, r.share_path);
      } else {
        nav(r.share_path);
      }
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Échec");
    } finally {
      setBusy(false);
    }
  };

  const voices = catalog?.voices;

  return (
    <div className="flex flex-col gap-7 text-charcoal">
      <div>
        <p className="font-mono text-[10.5px] uppercase tracking-wide text-concrete">
          Avatar · créateur
        </p>
        <h1 className="font-sans text-3xl sm:text-4xl font-semibold tracking-tight-grot leading-[1.05] mt-1">
          Sélection additive. Un batch.
        </h1>
      </div>

      {catalogErr && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {catalogErr} — images par défaut affichées.
        </p>
      )}

      {seededReuseVideoId && (
        <p className="text-xs text-charcoal bg-lemonade/30 border border-lemonade rounded-lg px-3 py-2 font-mono">
          Vidéo source pré-sélectionnée depuis « Videos received » ·{" "}
          <span className="font-bold">{seededReuseVideoId.slice(0, 12)}…</span>
        </p>
      )}

      {catalog?.sample_driving_video_url && (
        <p className="text-xs text-concrete font-mono break-all">
          Vidéo de mouvement de test (opérateur) :{" "}
          <a
            href={catalog.sample_driving_video_url}
            className="underline text-charcoal"
            target="_blank"
            rel="noreferrer"
          >
            {catalog.sample_driving_video_url}
          </a>
        </p>
      )}

      <section className="flex flex-col gap-3 rounded-lg border border-stone bg-paper p-4">
        <h2 className="font-mono text-[10.5px] uppercase text-concrete">
          <span className="mr-2">00</span>
          <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
            Vidéo miroir (mouvement)
          </span>
        </h2>
        <p className="text-xs text-concrete -mt-1">
          Le pipeline utilise la vidéo comme référence de mouvement (Kling). Tu peux en enregistrer
          une nouvelle ou en reprendre une déjà prête dans Videolink.
        </p>
        <div className="flex flex-col gap-2 text-sm">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="videoSource"
              checked={videoSource === "new"}
              onChange={() => {
                setVideoSource("new");
                setReuseVideoId(null);
              }}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-charcoal">Nouvel enregistrement</span>
              <span className="block text-concrete text-xs mt-0.5">
                Un lien dédié sera créé ; filme-toi puis lance la génération depuis la page session.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="radio"
              name="videoSource"
              checked={videoSource === "reuse"}
              onChange={() => setVideoSource("reuse")}
              className="mt-1"
            />
            <span>
              <span className="font-medium text-charcoal">Réutiliser une vidéo récente</span>
              <span className="block text-concrete text-xs mt-0.5">
                Les 10 dernières vidéos marquées « ready » dans l&apos;admin VideoLink.
              </span>
            </span>
          </label>
        </div>

        {videoSource === "reuse" && (
          <div className="mt-1 space-y-2">
            {recentVideosErr && (
              <p className="text-sm text-red-700">{recentVideosErr}</p>
            )}
            {recentVideos === null && !recentVideosErr && (
              <p className="text-sm text-concrete">Chargement des vidéos…</p>
            )}
            {recentVideos && recentVideos.length === 0 && (
              <p className="text-sm text-concrete">
                Aucune vidéo prête pour le moment — enregistre-en une via un lien VideoLink puis
                reviens ici.
              </p>
            )}
            {recentVideos && recentVideos.length > 0 && (
              <ul className="max-h-56 overflow-y-auto rounded-lg border border-stone divide-y divide-stone bg-white">
                {recentVideos.map((v) => (
                  <li key={v.video_id}>
                    <button
                      type="button"
                      onClick={() => setReuseVideoId(v.video_id)}
                      className={`w-full text-left px-3 py-2.5 text-sm transition ${
                        reuseVideoId === v.video_id
                          ? "bg-ivory-tint font-medium"
                          : "hover:bg-ivory"
                      }`}
                    >
                      <div className="truncate">
                        {v.link_title || "Sans titre"}{" "}
                        <span className="font-mono text-xs text-concrete">
                          · {relativeTime(v.uploaded_at)}
                        </span>
                      </div>
                      <div className="text-xs text-concrete font-mono mt-0.5">
                        {v.size_bytes != null ? formatBytes(v.size_bytes) : "—"} · {v.video_id.slice(0, 8)}…
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <p className="text-sm text-concrete -mt-2">
        Le texte sert au téléprompteur et à la synthèse vocale (une passe par langue sélectionnée)
        + lip-sync.
      </p>
      <textarea
        value={script}
        onChange={(e) => setScript(e.target.value)}
        rows={3}
        className="w-full rounded-lg border border-stone bg-paper px-3 py-2 text-sm font-sans"
        placeholder="Script…"
      />

      <label className="flex items-center gap-2 text-sm text-concrete cursor-pointer select-none">
        <input
          type="checkbox"
          checked={advancedScripts}
          onChange={(e) => setAdvancedScripts(e.target.checked)}
        />
        Texte différent par langue (optionnel)
      </label>

      {advancedScripts && (
        <div className="space-y-3 rounded-lg border border-stone bg-paper p-3">
          {langs.map((code) => {
            const label = languageOptions.find((o) => o.code === code)?.label ?? code;
            return (
              <label key={code} className="block text-sm">
                <span className="text-concrete text-xs font-mono uppercase">{label}</span>
                <textarea
                  value={byLang[code] ?? ""}
                  onChange={(e) =>
                    setByLang((m) => ({ ...m, [code]: e.target.value }))
                  }
                  rows={2}
                  placeholder={`Script ${code} (vide = script principal)`}
                  className="mt-1 w-full rounded border border-stone px-2 py-1.5 text-sm"
                />
              </label>
            );
          })}
        </div>
      )}


      <PersonaPicker
        selected={personIds}
        onChange={(next) =>
          setPersonIds(next.length ? next : [PERSONA_CATALOG[0].id])
        }
        bgIds={bgIds}
        sectionNumber="01"
        allowEmpty={false}
      />

      <BackgroundPicker
        selected={bgIds}
        onChange={(next) =>
          setBgIds(next.length ? next : [BG_CATALOG[0].id])
        }
        personIds={personIds}
        sectionNumber="02"
        allowEmpty={false}
      />

      {/* 03 Voix — single (1 par persona) */}
      <section className="flex flex-col gap-1.5">
        <h2 className="font-mono text-[10.5px] uppercase text-concrete mb-1">
          <span className="mr-2">03</span>
          <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
            Voix
          </span>
        </h2>
        {previewErr && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5">
            {previewErr}
          </p>
        )}
        {VOICE_ORDER.map((vid) => {
          const meta = voices?.[vid] ?? VOICE_FALLBACK[vid];
          const name = meta?.name ?? vid;
          const sub = meta?.meta ?? "";
          const isPlaying = previewingVoice === vid;
          const isLoading = previewLoading === vid;
          return (
            <div
              key={vid}
              role="button"
              tabIndex={0}
              onClick={() => setVoice(vid)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setVoice(vid);
                }
              }}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border bg-paper text-left cursor-pointer transition ${
                voice === vid
                  ? "border-[1.5px] border-charcoal bg-ivory-tint"
                  : "border-stone"
              }`}
            >
              <div className="flex justify-center shrink-0 w-6">
                <div
                  className={`w-3.5 h-3.5 rounded-full border-2 border-charcoal flex items-center justify-center ${
                    voice === vid ? "" : "opacity-40"
                  }`}
                >
                  {voice === vid && (
                    <div className="w-1.5 h-1.5 rounded-full bg-charcoal" />
                  )}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-sans text-sm font-semibold">{name}</div>
                <div className="font-mono text-[10.5px] text-concrete lowercase">{sub}</div>
              </div>
              <div className="hidden sm:block w-28 shrink-0">
                <MiniWaveform active={voice === vid || isPlaying} />
              </div>
              <button
                type="button"
                onClick={(e) => void toggleVoicePreview(vid, e)}
                disabled={isLoading}
                title="Écouter un extrait (ElevenLabs)"
                className="w-9 h-9 shrink-0 border border-stone rounded-lg flex items-center justify-center font-mono text-xs text-charcoal bg-paper hover:bg-ivory-tint disabled:opacity-50"
                aria-label={isPlaying ? "Arrêter l’extrait" : "Écouter un extrait"}
              >
                {isLoading ? "…" : isPlaying ? "■" : "▶"}
              </button>
            </div>
          );
        })}
      </section>

      {/* 04 Langues — déjà multi */}
      <section className="flex flex-col gap-3">
        <h2 className="font-mono text-[10.5px] uppercase text-concrete flex items-center gap-2">
          <span>04</span>
          <span className="font-sans text-[15px] font-semibold text-charcoal normal-case">
            Langues
          </span>
          <span className="ml-auto font-mono text-[10.5px] text-concrete normal-case">
            {langs.length} sélectionnée{langs.length > 1 ? "s" : ""}
          </span>
        </h2>
        <p className="text-xs text-concrete -mt-1">
          Sélectionne une ou plusieurs langues — le pipeline produit une vidéo finale par langue.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {languageOptions.map((l) => {
            const on = langs.includes(l.code);
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => toggleLang(l.code)}
                className={`h-8 px-3.5 text-[11px] font-mono uppercase rounded border ${
                  on
                    ? "bg-charcoal text-ivory border-charcoal"
                    : "border-charcoal bg-transparent text-charcoal opacity-70 hover:opacity-100"
                }`}
              >
                {l.code}
              </button>
            );
          })}
        </div>
      </section>

      {/* Récap matrice avec mini-thumbs des composites */}
      <section className="rounded-lg border border-charcoal bg-ivory-tint p-3 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-charcoal font-mono leading-relaxed">
            <span className="uppercase tracking-wide text-concrete">À générer · </span>
            {personIds.length} personnage{personIds.length > 1 ? "s" : ""}
            {" × "}
            {bgIds.length} fond{bgIds.length > 1 ? "s" : ""}
            {" × "}
            {langs.length} langue{langs.length > 1 ? "s" : ""}
          </div>
          <div className="font-mono text-2xl font-bold text-charcoal tabular-nums">
            {jobCount}
          </div>
        </div>
        {personIds.length > 0 && bgIds.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10.5px] uppercase tracking-wide text-concrete">
                Aperçu des combinaisons
              </span>
              {personIds.length * bgIds.length > 8 && (
                <span className="font-mono text-[10.5px] text-concrete">
                  + {personIds.length * bgIds.length - 8} autres
                </span>
              )}
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
              {personIds
                .flatMap((pid) => bgIds.map((bid) => ({ pid, bid })))
                .slice(0, 8)
                .map(({ pid, bid }) => (
                  <div
                    key={`${pid}__${bid}`}
                    className="aspect-square rounded overflow-hidden border border-stone bg-stone/30 relative"
                    style={{
                      backgroundImage: `url(/avatar-assets/backgrounds/${bid}.jpg)`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                    title={`${PERSONA_CATALOG.find((p) => p.id === pid)?.name} · ${
                      BG_CATALOG.find((b) => b.id === bid)?.name
                    }`}
                  >
                    <img
                      src={`/avatar-assets/persons-matted/${pid}.png`}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover object-bottom"
                    />
                  </div>
                ))}
            </div>
          </div>
        )}
      </section>

      {err && <p className="text-sm text-red-700">{err}</p>}

      <button
        type="button"
        disabled={
          busy ||
          !script.trim() ||
          langs.length === 0 ||
          personIds.length === 0 ||
          bgIds.length === 0 ||
          (videoSource === "reuse" && !reuseVideoId)
        }
        onClick={submit}
        className="h-[52px] w-full bg-charcoal text-ivory font-mono text-xs uppercase tracking-wider rounded-lg disabled:opacity-50"
      >
        {busy
          ? "Génère…"
          : isBatch
          ? `Lancer batch — ${jobCount} vidéos (preview)`
          : "Générer le lien"}
      </button>

      {isBatch && (
        <p className="text-[10.5px] text-concrete font-mono -mt-3">
          Preview UI · le backend batch sera branché au Bloc 1. Pour générer maintenant, réduis à 1
          personnage et 1 fond.
        </p>
      )}
    </div>
  );
}
